import { zodResolver } from "@hookform/resolvers/zod";
import { KeyRound, ShieldAlert } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { authApi } from "@/lib/api/endpoints";
import { applyApiError } from "@/lib/form-errors";
import { AuthLayout } from "./auth-layout";

const schema = z
  .object({
    password: z.string().min(10, "Use at least 10 characters"),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ["confirm"],
    message: "Those don't match",
  });

type Values = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    form.clearErrors("root");
    try {
      await authApi.resetPassword({ token, password: values.password });
      toast.success("Password updated", {
        description: "Every device has been signed out. Sign in with your new password.",
      });
      navigate("/login", { replace: true });
    } catch (error) {
      applyApiError(error, form);
    }
  });

  if (!token) {
    return (
      <AuthLayout title="That link isn't complete" subtitle="The reset link is missing its token.">
        <Alert variant="warning">
          <ShieldAlert />
          <AlertTitle>Request a new link</AlertTitle>
          <AlertDescription>
            Reset links can only be used once and expire quickly. Start again and we'll send a fresh
            one.
          </AlertDescription>
        </Alert>
        <Button asChild className="mt-5 w-full">
          <Link to="/forgot-password">Send a new link</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set a new password" subtitle="This signs you out everywhere else.">
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {form.formState.errors.root?.message && (
          <Alert variant="destructive">
            <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
          </Alert>
        )}
        <Field
          label="New password"
          htmlFor="password"
          error={form.formState.errors.password?.message}
          hint="At least 10 characters."
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            autoFocus
            {...form.register("password")}
          />
        </Field>
        <Field label="Confirm password" htmlFor="confirm" error={form.formState.errors.confirm?.message}>
          <Input id="confirm" type="password" autoComplete="new-password" {...form.register("confirm")} />
        </Field>
        <Button type="submit" size="lg" className="w-full" loading={form.formState.isSubmitting}>
          <KeyRound className="size-4" />
          Update password
        </Button>
      </form>
    </AuthLayout>
  );
}
